import { Control, Controller, FieldValues, Path } from "react-hook-form";

import { FormItem } from "src/base-components/FormItem";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import { Callout } from "src/design-system/Callout";

const ToggleText = {
  database: {
    envAwareLabel:
      "Allow authors to hit production environment during testing?",
    defaultLabel: "Allow authors to hit this connection during testing?",
    helpText:
      "This allows Decision Flow authors to call this database in test runs and sandbox Job runs",
  },
  webhook: {
    envAwareLabel:
      "Allow to use production environment in sandbox flow executions?",
    defaultLabel: "Allow to use this connection in sandbox flow executions?",
    helpText:
      "This allows to wait for Inbound Webhook during sandbox flow executions",
  },
  llm: {
    envAwareLabel: "Allow this key to be used in sandbox flow executions?",
    defaultLabel: "Allow this key to be used in sandbox flow executions?",
    helpText:
      "When sandbox credentials are not configured, this allows the production key to be used in sandbox runs.",
  },
};

type TestInvocationToggleProps<T extends FieldValues> = {
  enableNonProdConfigs: boolean;
  control: Control<T>;
  type: keyof typeof ToggleText;
  /** Form path of the allow-test-invocations flag. */
  name: Path<T>;
};

export const TestInvocationToggle = <T extends FieldValues>({
  enableNonProdConfigs,
  control,
  type,
  name,
}: TestInvocationToggleProps<T>) => {
  const toggleText = ToggleText[type];

  // When sandbox credentials are configured, sandbox runs always use them, so
  // allow_test_invocations is dormant (it only gates the production-key fallback
  // used when no sandbox config exists).
  const helpText =
    type === "llm" && enableNonProdConfigs
      ? "Sandbox runs use your sandbox key. This setting only applies as a fallback if the sandbox key is later removed."
      : toggleText.helpText;

  // The production-key warning only applies to LLM credentials with no separate
  // sandbox key configured, when the production-key fallback is enabled.
  const isProdKeyFallbackActive = type === "llm" && !enableNonProdConfigs;

  return (
    <FormItem
      gap="sm"
      label={
        enableNonProdConfigs
          ? toggleText.envAwareLabel
          : toggleText.defaultLabel
      }
      isRequired
    >
      <div className="mb-4 font-inter-normal-12px text-gray-500">
        {helpText}
      </div>
      <Controller
        control={control}
        name={name}
        render={(props) => (
          <div className="flex flex-col gap-2">
            <SimpleRadioGroup
              orientation="vertical"
              value={String(props.field.value)}
              onValueChange={(value) => {
                props.field.onChange(value === "true");
              }}
            >
              <SimpleRadioGroup.Item
                label="No, do not allow"
                labelClassName="pl-2.5"
                value="false"
              />
              <SimpleRadioGroup.Item
                label="Yes, allow"
                labelClassName="pl-2.5"
                value="true"
              />
            </SimpleRadioGroup>
            {isProdKeyFallbackActive && props.field.value === true && (
              <Callout variant="warning" dataLoc="llm-prod-key-sandbox-warning">
                Sandbox flow runs will use your <b>production</b> API key -
                consuming its quota and billing. Anyone who can run sandbox
                flows can indirectly use this key. Configure separate sandbox
                credentials to avoid this.
              </Callout>
            )}
          </div>
        )}
      />
    </FormItem>
  );
};
